import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SERVICES } from "@/content";
import { MaskLines, Overline, Reveal } from "@/components/site/Reveal";

const Meta = ({ s }) => (
  <div className="mt-8 grid grid-cols-1 gap-6 border-t border-rc-line pt-6 sm:grid-cols-2">
    <div>
      <p className="font-mono-rc text-[11px] uppercase tracking-[0.22em] text-rc-copper">Best for</p>
      <p className="mt-2 text-sm leading-relaxed text-rc-body">{s.bestFor}</p>
    </div>
    <div>
      <p className="font-mono-rc text-[11px] uppercase tracking-[0.22em] text-rc-copper">Pricing</p>
      <p className="mt-2 text-sm leading-relaxed text-rc-body">{s.pricing}</p>
    </div>
  </div>
);

const Items = ({ items }) => (
  <ul className="mt-6 space-y-2.5">
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-sm text-rc-ink2 sm:text-base">
        <Check className="mt-1 h-4 w-4 shrink-0 text-rc-copper" />
        {it}
      </li>
    ))}
  </ul>
);

const Media = ({ s, className }) => (
  <div className={`relative overflow-hidden ${className}`}>
    <img src={s.image} alt={s.title} style={s.pos ? { objectPosition: s.pos } : undefined} className="h-full w-full object-cover object-[50%_30%] grayscale transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
  </div>
);

const Card = ({ s, className = "", children, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      className="group flex h-full flex-col overflow-hidden rounded-[28px] border border-rc-line bg-rc-surface transition-[border-color] duration-500 hover:border-rc-copper/60"
      data-testid={`service-card-${s.id}`}
    >
      {children}
    </motion.article>
  </Reveal>
);

const Body = ({ s, children }) => (
  <div className="flex flex-1 flex-col p-7 sm:p-9">
    <h3 className="font-display text-3xl font-medium leading-tight text-rc-ink sm:text-4xl">{s.title}</h3>
    <p className="mt-4 text-base leading-relaxed text-rc-body">{s.intro}</p>
    {children}
  </div>
);

export const Services = () => {
  const [consult, production, worship] = SERVICES;
  return (
    <section id="services" className="relative mx-auto max-w-[1440px] px-5 py-28 sm:px-8 lg:px-12 lg:py-40" data-testid="services-section">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <Overline>Services Offered</Overline>
          <MaskLines
            lines={["Customized solutions,", <>built around <em className="italic text-rc-copper">your</em> goals.</>]}
            className="mt-8 font-display text-4xl font-medium leading-[1.02] tracking-tight text-rc-ink sm:text-5xl lg:text-6xl"
          />
        </div>
        <Reveal className="max-w-md text-base leading-relaxed text-rc-body">
          We partner with churches, artists, and creative organizations to develop leaders, strengthen creative teams, and bring musical visions to life.
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:grid-rows-[auto_auto]">
        <Card s={consult} className="lg:col-span-7">
          <div className="grid h-full grid-cols-1 md:grid-cols-5">
            <Media s={consult} className="h-96 md:col-span-2 md:h-auto" />
            <div className="md:col-span-3 flex">
              <Body s={consult}>
                <Items items={consult.items} />
                <Meta s={consult} />
              </Body>
            </div>
          </div>
        </Card>

        <Card s={production} className="lg:col-span-5 lg:row-span-2" delay={0.1}>
          <Media s={production} className="h-72 sm:h-80 lg:h-96" />
          <Body s={production}>
            <Items items={production.items} />
            <Meta s={production} />
          </Body>
        </Card>

        <Card s={worship} className="lg:col-span-7" delay={0.15}>
          <div className="grid h-full grid-cols-1 md:grid-cols-5">
            <div className="order-2 flex md:order-1 md:col-span-3">
              <Body s={worship}>
                <Items items={worship.items} />
                <Meta s={worship} />
              </Body>
            </div>
            <Media s={worship} className="order-1 h-96 md:order-2 md:col-span-2 md:h-auto" />
          </div>
        </Card>
      </div>
    </section>
  );
};
